import React, {useState, useRef} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { EAuthData } from '../Provider/EAuthProvider';
import { Logo } from '../../components/images';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const EmployerSignin = () => {
    const authContext = EAuthData();
    const [showPassword, setShowPassword] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const errorRef = useRef(''); // Use ref to persist error across rerenders
    const [forceUpdate, setForceUpdate] = useState(0); // Force component update // Moved outside form
    
    const { 
        employerLogin, 
        login, // Fallback if employerLogin not available
        employerVerificationStatus,
        user
    } = authContext;
    
    const navigate = useNavigate();
    
    const handleLogin = async (values, { setSubmitting, setErrors }) => {
        try {
            // Clear any previous errors
            setGeneralError('');
            errorRef.current = '';
            
            // Handle remember me functionality
            if (values.rememberMe) {
                localStorage.setItem('email', values.email);
            } else {
                localStorage.removeItem('email');
                localStorage.removeItem('password');
            }

            let user;
            
            // Try employerLogin first, fallback to regular login
            if (employerLogin) {
                console.log('Using employerLogin method');
                user = await employerLogin(values.email, values.password);
            } else {
                console.log('Using regular login method (fallback)');
                user = await login(values);
            }
            
            console.log('Login successful:', user?.displayName);


            // Navigate to employer dashboard on success
            navigate('/employer/');
            
        } catch (error) {
            console.error('Login error:', error);
            
            let errorMessage = '';
            
            // Handle specific verification errors
            if (error.message.includes('pending verification') || error.message.includes('pending')) {
                errorMessage = 'Your account is pending verification. Please wait for approval.';
            } else if (error.message.includes('rejected')) {
                errorMessage = 'Your account application was rejected. Please contact support.';
            } else if (error.message.includes('not active')) {
                errorMessage = 'Your account is not active. Please contact support.';
            } else if (error.message.includes('not found')) {
                errorMessage = 'No employer account found. Please register first.';
            } else if (error.message.includes('user-not-found')) {
                errorMessage = 'No account found with this email address.';
            } else if (error.message.includes('wrong-password')) {
                errorMessage = 'Incorrect password. Please try again.';
            } else if (error.message.includes('invalid-email')) {
                errorMessage = 'Invalid email address format.';
            } else if (error.message.includes('too-many-requests')) {
                errorMessage = 'Too many failed attempts. Please try again later.';
            } else if (error.message.includes('application-submitted')) {
                // This will be thrown by the updated login method for inactive accounts
                navigate('/employer/application-submitted');
                return;
            } else {
                errorMessage = 'Login failed. Check credentials and try again.';
            }
            
            // Set error in both state, ref, and force update
            setGeneralError(errorMessage);
            errorRef.current = errorMessage;
            setForceUpdate(prev => prev + 1); // Force component to show error
            
        } finally {
            setSubmitting(false);
        }

    };

    const validationSchema = Yup.object({
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required'),
        password: Yup.string()
            .required('Password is required')
    });

    return (
        <div className='bg-gradient-to-br from-orange-200 to-orange-500 min-h-screen w-screen p-4 md:p-10'>
            {/* Header */}
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8'>
                <Link to="/main-signup">
                    <div className='flex flex-row items-center mb-4 sm:mb-0'>
                        <img src={Logo} className='w-8 h-8 sm:w-10 sm:h-10' alt="Logo"/>
                        <p className='text-lg sm:text-xl lg:text-2xl ml-2 font-medium text-gray-800'>Oodles Internships</p>
                    </div>
                </Link>
                
                <div className='flex sm:flex-row justify-center gap-2 sm:gap-3'>               
                    <Link to='/about'                             
                    className='text-md sm:text-md hover:underline'               
                    >                 
                        About               
                    </Link>               
                    <Link to='/faq'                              
                    className='text-md sm:text-md hover:underline'               
                    >                 
                        FAQs               
                    </Link>             
                </div>
            </div>
            
            <div className="flex justify-center items-center">    
                <div className='flex flex-col gap-6 p-8 md:p-12 bg-white shadow-2xl rounded-xl max-w-md w-full'>
                    {/* Header */}
                    <div className='text-center'>
                        <h2 className='text-2xl font-bold text-orange-400 mb-2'>Employer Sign In</h2>
                        <p className='text-gray-600'>Welcome back! Please sign in to your account.</p>
                    </div>
                    
                    {/* General Error Message - OUTSIDE OF FORM with ref backup */}
                    {(generalError || errorRef.current) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-red-800 text-sm text-center">
                                {generalError || errorRef.current}
                            </p>
                        </div>
                    )}
                    
                    {/* Login Form */}
                    <Formik  
                        initialValues={{
                            email: localStorage.getItem('email') || '',
                            password: '',
                            rememberMe: Boolean(localStorage.getItem('email')),
                        }}
                        validationSchema={validationSchema}
                        onSubmit={handleLogin}
                    >
                        {({ errors, isSubmitting, touched }) => (
                            <Form className='space-y-4'>
                                {/* Email Field */}
                                <div>
                                    <label htmlFor='email' className='block text-sm font-medium text-gray-700 mb-2'>
                                        Company Email
                                    </label>
                                    <Field 
                                        className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500' 
                                        type='email' 
                                        name="email"
                                        id="email"
                                        placeholder='Enter your company email' 
                                    />
                                    <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                                </div>

                                {/* Password Field */}
                                <div>
                                    <label htmlFor='password' className='block text-sm font-medium text-gray-700 mb-2'>
                                        Password
                                    </label>
                                    <div className="relative">
                                        <Field
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            id ="password"
                                            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500'
                                            placeholder="Enter your password"
                                        />
                                        <button
                                            type="button"
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? (
                                                <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                            ) : (
                                                <FaEye className="h-5 w-5 text-gray-400" />
                                            )}
                                        </button>
                                    </div>
                                    <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
                                </div>

                                {/* Remember Me & Forgot Password */}
                                <div className='flex justify-between items-center'>
                                    <label className='flex items-center'>
                                        <Field 
                                            type="checkbox" 
                                            name="rememberMe" 
                                            className='h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500'
                                        />
                                        <span className='ml-2 text-sm text-gray-700'>Remember me</span>
                                    </label>
                                    <Link 
                                        to='/forgot-password' 
                                        className='text-sm text-orange-600 hover:text-orange-800 font-medium'
                                    >
                                        Forgot Password?
                                    </Link>
                                </div>

                                {/* Submit Button */}
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting} 
                                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-md transition-colors duration-200 flex items-center justify-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            Signing in...
                                        </>
                                    ) : (
                                        'Sign In'
                                    )}
                                </button>

                                {/* Sign Up Link */}
                                <div className='text-center pt-4 border-t border-gray-200'>
                                    <p className='text-sm text-gray-600'>
                                        Don't have an account?{' '}
                                        <Link 
                                            to="/employer/register" 
                                            className='text-orange-600 hover:text-orange-800 font-medium'
                                        >
                                            Register here
                                        </Link>
                                    </p>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>

            {/* Help Section */}
            <div className='text-center mt-8'>
                <p className='text-gray-600 text-sm'>
                    Need help? Contact us at{' '}
                    <a 
                        href="mailto:info.oodlestech@gmail.com" 
                        className='text-orange-600 hover:text-orange-800 font-medium'
                    >
                        info.oodlestech@gmail.com
                    </a>
                </p>
            </div>
        </div>
    );
};

export default EmployerSignin;